import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import {
  Save,
  Download,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  MousePointer2,
  Move,
  Type,
  Image,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Trash2,
  Plus,
  Layers,
  Settings2,
  ChevronLeft,
  ChevronRight,
  Upload,
  RotateCcw,
  Loader2,
  X,
  Sparkles,
} from 'lucide-react'
import { useEditorStore, Layer } from '@/stores/editorStore'
import FabricCanvas, { FabricCanvasRef } from '@/components/editor/FabricCanvas'
import { coversApi, Palette, palettesApi } from '@/services/api'
import { AiGenerationPanel } from '@/components/AiGenerationPanel'
import clsx from 'clsx'

const COVER_PRESETS = [
  { name: '네이버 시리즈', width: 750, height: 1100 },
  { name: '카카오 페이지', width: 720, height: 1080 },
  { name: '리디북스', width: 750, height: 1050 },
  { name: '정사각형', width: 800, height: 800 },
]

const FONT_FAMILIES = [
  'Noto Sans KR',
  'Noto Serif KR',
  'Black Han Sans',
  'Do Hyeon',
  'Jua',
  'Gothic A1',
  'Nanum Gothic',
  'Nanum Myeongjo',
]

export default function EditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, points } = useAuthStore()
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  const [showAssetBrowserModal, setShowAssetBrowserModal] = useState(false)
  const [assetBrowserType, setAssetBrowserType] = useState<'cover' | 'background' | 'character' | 'typography' | 'effect'>('background')
  const [activeTab, setActiveTab] = useState<'editor' | 'ai'>('editor')

  // 로그인 필수 - 비로그인시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [showNewCoverModal, setShowNewCoverModal] = useState(!id)
  const [customWidth, setCustomWidth] = useState(750)
  const [customHeight, setCustomHeight] = useState(1100)
  const [showExportModal, setShowExportModal] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<FabricCanvasRef>(null)

  // Palette state
  const [palette, setPalette] = useState<Palette | null>(null)
  const [paletteLoading, setPaletteLoading] = useState(false)

  const {
    cover,
    setCover,
    selectedLayerId,
    setSelectedLayer,
    addLayer,
    updateLayer,
    removeLayer,
    zoom,
    setZoom,
    tool,
    setTool,
    undo,
    redo,
    history,
    historyIndex,
    hasUnsavedChanges,
    resetEditor,
  } = useEditorStore()

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault()
            if (e.shiftKey) redo()
            else undo()
            break
          case 'y':
            e.preventDefault()
            redo()
            break
          case 's':
            e.preventDefault()
            handleSave()
            break
          case '=':
          case '+':
            e.preventDefault()
            setZoom(zoom + 0.1)
            break
          case '-':
            e.preventDefault()
            setZoom(zoom - 0.1)
            break
        }
      } else {
        switch (e.key) {
          case 'v':
            setTool('select')
            break
          case 'm':
            setTool('move')
            break
          case 't':
            setTool('text')
            break
          case 'Delete':
          case 'Backspace':
            if (selectedLayerId) {
              removeLayer(selectedLayerId)
            }
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [zoom, selectedLayerId, undo, redo, setZoom, setTool, removeLayer])

  // Fetch palette function (can be called to refresh)
  const fetchPalette = useCallback(async () => {
    if (!id) return

    setPaletteLoading(true)
    try {
      const result = await coversApi.getPalette(id)
      if (result.success && result.data) {
        setPalette(result.data)
      }
    } catch (err) {
      console.error('Failed to fetch palette:', err)
    } finally {
      setPaletteLoading(false)
    }
  }, [id])

  // Fetch cover's palette when cover loads
  useEffect(() => {
    fetchPalette()
  }, [fetchPalette])

  // Load existing cover
  useEffect(() => {
    const loadCover = async () => {
      if (id) {
        try {
          const result = await coversApi.get(id)
          if (result.success && result.data) {
            setCover({
              id: result.data.id,
              title: result.data.title,
              width: result.data.width,
              height: result.data.height,
              backgroundColor: result.data.data.canvas.backgroundColor || '#1a1a1a',
              layers: (result.data.data.layers || []) as Layer[],
              createdAt: result.data.created_at,
              updatedAt: result.data.updated_at,
            })
          }
        } catch (err) {
          console.error('Failed to load cover:', err)
          // Fallback to empty cover
          setCover({
            id,
            title: '로딩 실패',
            width: 750,
            height: 1100,
            backgroundColor: '#1a1a1a',
            layers: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        }
      }
    }

    loadCover()

    return () => {
      resetEditor()
    }
  }, [id])

  // Update document title when cover title changes
  useEffect(() => {
    if (cover?.title) {
      document.title = `${cover.title} - AI Studio`
    }
    return () => {
      document.title = 'AI Studio'
    }
  }, [cover])

  // Listen for palette updates from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security check: verify origin
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'PALETTE_UPDATED') {
        // Refetch palette
        const fetchPalette = async () => {
          if (!id) return
          try {
            const result = await coversApi.getPalette(id)
            if (result.success && result.data) {
              setPalette(result.data)
            }
          } catch (err) {
            console.error('Failed to refresh palette:', err)
          }
        }
        fetchPalette()

        // Close modal
        setShowAssetBrowserModal(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [id])

  const handleCreateCover = (width: number, height: number) => {
    // Generate automatic title: "표지YYMMDD_HHMM"
    const now = new Date()
    const year = String(now.getFullYear()).slice(2) // YY
    const month = String(now.getMonth() + 1).padStart(2, '0') // MM
    const day = String(now.getDate()).padStart(2, '0') // DD
    const hour = String(now.getHours()).padStart(2, '0') // HH
    const minute = String(now.getMinutes()).padStart(2, '0') // MM
    const autoTitle = `표지${year}${month}${day}_${hour}${minute}`

    setCover({
      id: `new-${Date.now()}`,
      title: autoTitle,
      width,
      height,
      backgroundColor: '#1a1a1a',
      layers: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    setShowNewCoverModal(false)
  }

  const handleSave = async () => {
    if (!cover) return

    try {
      const coverData = {
        version: '1.0',
        canvas: {
          width: cover.width,
          height: cover.height,
          backgroundColor: cover.backgroundColor,
        },
        layers: cover.layers,
      }

      // Check if this is a new cover or existing one
      const isNewCover = cover.id.startsWith('new-')

      if (isNewCover) {
        // Create new cover
        const result = await coversApi.create({
          title: cover.title,
          width: cover.width,
          height: cover.height,
          data: coverData,
        })

        if (result.success && result.data) {
          // Update cover with the server-assigned ID
          setCover({
            ...cover,
            id: result.data.id,
            createdAt: result.data.created_at,
            updatedAt: result.data.updated_at,
          })

          // Navigate to the editor with the new ID
          navigate(`/editor/${result.data.id}`, { replace: true })
          alert('표지가 생성되었습니다.')
        } else {
          alert('저장 실패: ' + (result.error?.message || '알 수 없는 오류'))
        }
      } else {
        // Update existing cover
        const result = await coversApi.update(cover.id, {
          title: cover.title,
          data: coverData,
        })

        if (result.success) {
          alert('저장되었습니다.')
        } else {
          alert('저장 실패: ' + (result.error?.message || '알 수 없는 오류'))
        }
      }
    } catch (err) {
      console.error('Save error:', err)
      alert('저장 중 오류가 발생했습니다.')
    }
  }

  const handleExport = (format: 'png' | 'jpeg', quality = 1) => {
    // Use canvas ref to export without selection controls
    if (!canvasRef.current) return

    const dataUrl = canvasRef.current.exportToDataURL(format, quality)
    if (!dataUrl) return

    const link = document.createElement('a')
    link.download = `${cover?.title || 'cover'}.${format}`
    link.href = dataUrl
    link.click()

    setShowExportModal(false)
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const img = new window.Image()
      img.onload = () => {
        addLayer({
          type: 'image',
          name: file.name.replace(/\.[^/.]+$/, ''),
          visible: true,
          locked: false,
          x: 50,
          y: 50,
          width: Math.min(img.width, cover?.width || 750),
          height: Math.min(img.height, cover?.height || 1100),
          rotation: 0,
          opacity: 1,
          imageUrl: reader.result as string,
        })
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()

    // Check if it's a palette asset
    const assetId = e.dataTransfer.getData('assetId')
    const assetUrl = e.dataTransfer.getData('assetUrl')

    if (assetId && assetUrl) {
      // Dropped from palette
      const img = new window.Image()
      img.onload = () => {
        addLayer({
          type: 'image',
          name: assetId,
          visible: true,
          locked: false,
          x: 50,
          y: 50,
          width: Math.min(img.width, cover?.width || 750),
          height: Math.min(img.height, cover?.height || 1100),
          rotation: 0,
          opacity: 1,
          imageUrl: assetUrl,
        })
      }
      img.src = assetUrl
      return
    }

    // Handle file drop
    const file = e.dataTransfer.files[0]
    if (!file?.type.startsWith('image/')) return

    const reader = new FileReader()
    reader.onload = () => {
      const img = new window.Image()
      img.onload = () => {
        addLayer({
          type: 'image',
          name: file.name.replace(/\.[^/.]+$/, ''),
          visible: true,
          locked: false,
          x: 50,
          y: 50,
          width: Math.min(img.width, cover?.width || 750),
          height: Math.min(img.height, cover?.height || 1100),
          rotation: 0,
          opacity: 1,
          imageUrl: reader.result as string,
        })
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  }, [addLayer, cover])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  // Remove asset from palette
  const handleRemoveAsset = useCallback(async (assetId: string) => {
    if (!palette?.id) return

    const confirmed = window.confirm('이 에셋을 팔레트에서 제거하시겠습니까?')
    if (!confirmed) return

    try {
      const result = await palettesApi.removeAsset(palette.id, assetId)
      if (result.success) {
        // Refresh palette to show updated asset list
        await fetchPalette()
      } else {
        alert(`에셋 제거 실패: ${result.error?.message || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to remove asset:', err)
      alert('에셋 제거 중 오류가 발생했습니다.')
    }
  }, [palette?.id, fetchPalette])

  // Handle AI generation complete
  const handleAiGenerationComplete = useCallback(async (_assetId: string) => {
    // Refresh palette to include the newly generated asset
    await fetchPalette()
    // Optionally switch back to editor tab
    // setActiveTab('editor')
  }, [fetchPalette])

  const selectedLayer = cover?.layers.find((l) => l.id === selectedLayerId)

  // New Cover Modal
  if (showNewCoverModal) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center p-4">
        <div className="card max-w-lg w-full p-6">
          <h2 className="text-2xl font-bold mb-6">새 표지 만들기</h2>

          <div className="space-y-4">
            <p className="text-text-secondary">표지 크기를 선택하세요</p>

            <div className="grid grid-cols-2 gap-3">
              {COVER_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handleCreateCover(preset.width, preset.height)}
                  className="p-4 border border-border-default rounded-lg hover:border-accent-gold hover:bg-bg-elevated transition-colors text-left"
                >
                  <div className="font-medium">{preset.name}</div>
                  <div className="text-sm text-text-tertiary">
                    {preset.width} x {preset.height}
                  </div>
                </button>
              ))}
            </div>

            <div className="pt-4 border-t border-border-subtle">
              <p className="text-sm text-text-secondary mb-3">커스텀 크기</p>
              <div className="flex gap-3">
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Number(e.target.value))}
                  placeholder="너비"
                  className="input flex-1"
                  min={256}
                  max={4096}
                />
                <span className="flex items-center text-text-tertiary">x</span>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Number(e.target.value))}
                  placeholder="높이"
                  className="input flex-1"
                  min={256}
                  max={4096}
                />
              </div>
              <button
                onClick={() => handleCreateCover(customWidth, customHeight)}
                className="btn btn-primary w-full mt-3"
              >
                생성하기
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!cover) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center">
        <div className="text-text-secondary">로딩 중...</div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Toolbar */}
      <div className="h-12 bg-bg-secondary border-b border-border-subtle flex items-center justify-between px-4">
        {/* Left tools */}
        <div className="flex items-center gap-1">
          <button
            onClick={undo}
            disabled={historyIndex <= 0}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="실행취소 (Ctrl+Z)"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="다시실행 (Ctrl+Y)"
          >
            <Redo2 className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-border-subtle mx-2" />

          <button
            onClick={() => setTool('select')}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              tool === 'select' ? 'bg-accent-gold/10 text-accent-gold' : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
            )}
            title="선택 도구 (V)"
          >
            <MousePointer2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setTool('move')}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              tool === 'move' ? 'bg-accent-gold/10 text-accent-gold' : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
            )}
            title="이동 도구 (M)"
          >
            <Move className="w-4 h-4" />
          </button>
          <button
            onClick={() => setTool('text')}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              tool === 'text' ? 'bg-accent-gold/10 text-accent-gold' : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
            )}
            title="텍스트 도구 (T)"
          >
            <Type className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-border-subtle mx-2" />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg"
            title="이미지 추가"
          >
            <Upload className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        {/* Center - Title */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={cover.title}
            onChange={(e) => setCover({ ...cover, title: e.target.value })}
            className="bg-transparent border-none text-center font-medium focus:outline-none focus:ring-1 focus:ring-accent-gold rounded px-2 py-1"
          />
          <span className="text-xs text-text-tertiary">
            {cover.width} x {cover.height}
          </span>
          {hasUnsavedChanges && (
            <span className="text-xs text-accent-gold">●</span>
          )}
        </div>

        {/* Right tools */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-bg-elevated rounded-lg px-2 py-1">
            <button
              onClick={() => setZoom(zoom - 0.1)}
              disabled={zoom <= 0.1}
              className="p-1 text-text-secondary hover:text-text-primary disabled:opacity-50"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={() => setZoom(1)}
              className="text-sm w-12 text-center hover:text-accent-gold"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={() => setZoom(zoom + 0.1)}
              disabled={zoom >= 3}
              className="p-1 text-text-secondary hover:text-text-primary disabled:opacity-50"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          <div className="w-px h-6 bg-border-subtle mx-2" />

          <button onClick={handleSave} className="btn btn-secondary text-sm">
            <Save className="w-4 h-4 mr-1" />
            저장
          </button>
          <button onClick={() => setShowExportModal(true)} className="btn btn-primary text-sm">
            <Download className="w-4 h-4 mr-1" />
            내보내기
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="h-10 bg-bg-secondary border-b border-border-subtle flex items-center px-4 gap-2">
        <button
          onClick={() => setActiveTab('editor')}
          className={clsx(
            'px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2',
            activeTab === 'editor'
              ? 'bg-accent-gold text-white'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
          )}
        >
          <Layers className="w-4 h-4" />
          레이어 편집기
        </button>
        <button
          onClick={() => setActiveTab('ai')}
          className={clsx(
            'px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2',
            activeTab === 'ai'
              ? 'bg-accent-gold text-white'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
          )}
        >
          <Sparkles className="w-4 h-4" />
          AI 생성
        </button>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {activeTab === 'editor' ? (
          <>
            {/* Left Panel - Assets */}
        <aside className={clsx(
          'bg-bg-secondary border-r border-border-subtle flex flex-col transition-all duration-200',
          leftPanelOpen ? 'w-64' : 'w-0'
        )}>
          {leftPanelOpen && (
            <>
              {/* Header */}
              <div className="p-3 border-b border-border-subtle flex items-center justify-between">
                <h3 className="text-sm font-medium">에셋</h3>
                <button
                  onClick={() => setLeftPanelOpen(false)}
                  className="p-1 text-text-tertiary hover:text-text-primary"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>

              {/* Asset Categories */}
              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {paletteLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-accent-gold mb-3" />
                    <p className="text-sm text-text-secondary">로딩 중...</p>
                  </div>
                ) : (
                  <>
                    {/* Integrated Cover */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-text-secondary">통합 표지</h4>
                        <button
                          onClick={() => {
                            setAssetBrowserType('cover')
                            setShowAssetBrowserModal(true)
                          }}
                          className="text-xs text-accent-gold hover:text-accent-gold-light"
                        >
                          + 추가
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {palette?.assets?.filter(a => a.type === 'cover').map((asset) => (
                          <div
                            key={asset.id}
                            data-asset-id={asset.id}
                            data-asset-type={asset.type}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('assetId', asset.id)
                              e.dataTransfer.setData('assetUrl', `https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`)
                            }}
                            className="relative aspect-square bg-bg-elevated rounded-lg cursor-move hover:ring-2 hover:ring-accent-gold transition-all overflow-hidden group"
                            title={asset.name}
                          >
                            <img
                              src={`https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveAsset(asset.id)
                                }}
                                className="opacity-0 group-hover:opacity-100 p-1.5 bg-red-500 rounded-full hover:bg-red-600 transition-all"
                                title="팔레트에서 제거"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Background */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-text-secondary">배경</h4>
                        <button
                          onClick={() => {
                            setAssetBrowserType('background')
                            setShowAssetBrowserModal(true)
                          }}
                          className="text-xs text-accent-gold hover:text-accent-gold-light"
                        >
                          + 추가
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {palette?.assets?.filter(a => a.type === 'background').map((asset) => (
                          <div
                            key={asset.id}
                            data-asset-id={asset.id}
                            data-asset-type={asset.type}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('assetId', asset.id)
                              e.dataTransfer.setData('assetUrl', `https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`)
                            }}
                            className="relative aspect-square bg-bg-elevated rounded-lg cursor-move hover:ring-2 hover:ring-accent-gold transition-all overflow-hidden group"
                            title={asset.name}
                          >
                            <img
                              src={`https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-xs text-white text-center px-2 line-clamp-2">{asset.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveAsset(asset.id)
                              }}
                              className="absolute top-1 right-1 p-1 bg-accent-error/80 hover:bg-accent-error text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="제거"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Character */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-text-secondary">인물</h4>
                        <button
                          onClick={() => {
                            setAssetBrowserType('character')
                            setShowAssetBrowserModal(true)
                          }}
                          className="text-xs text-accent-gold hover:text-accent-gold-light"
                        >
                          + 추가
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {palette?.assets?.filter(a => a.type === 'character').map((asset) => (
                          <div
                            key={asset.id}
                            data-asset-id={asset.id}
                            data-asset-type={asset.type}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('assetId', asset.id)
                              e.dataTransfer.setData('assetUrl', `https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`)
                            }}
                            className="relative aspect-square bg-bg-elevated rounded-lg cursor-move hover:ring-2 hover:ring-accent-gold transition-all overflow-hidden group"
                            title={asset.name}
                          >
                            <img
                              src={`https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-xs text-white text-center px-2 line-clamp-2">{asset.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveAsset(asset.id)
                              }}
                              className="absolute top-1 right-1 p-1 bg-accent-error/80 hover:bg-accent-error text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="제거"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Typography */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-text-secondary">타이포</h4>
                        <button
                          onClick={() => {
                            setAssetBrowserType('typography')
                            setShowAssetBrowserModal(true)
                          }}
                          className="text-xs text-accent-gold hover:text-accent-gold-light"
                        >
                          + 추가
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {palette?.assets?.filter(a => a.type === 'title' || a.type === 'typography').map((asset) => (
                          <div
                            key={asset.id}
                            data-asset-id={asset.id}
                            data-asset-type={asset.type}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('assetId', asset.id)
                              e.dataTransfer.setData('assetUrl', `https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`)
                            }}
                            className="relative aspect-square bg-bg-elevated rounded-lg cursor-move hover:ring-2 hover:ring-accent-gold transition-all overflow-hidden group"
                            title={asset.name}
                          >
                            <img
                              src={`https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-xs text-white text-center px-2 line-clamp-2">{asset.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveAsset(asset.id)
                              }}
                              className="absolute top-1 right-1 p-1 bg-accent-error/80 hover:bg-accent-error text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="제거"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Effect */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-text-secondary">효과</h4>
                        <button
                          onClick={() => {
                            setAssetBrowserType('effect')
                            setShowAssetBrowserModal(true)
                          }}
                          className="text-xs text-accent-gold hover:text-accent-gold-light"
                        >
                          + 추가
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {palette?.assets?.filter(a => a.type === 'effect').map((asset) => (
                          <div
                            key={asset.id}
                            data-asset-id={asset.id}
                            data-asset-type={asset.type}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('assetId', asset.id)
                              e.dataTransfer.setData('assetUrl', `https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`)
                            }}
                            className="relative aspect-square bg-bg-elevated rounded-lg cursor-move hover:ring-2 hover:ring-accent-gold transition-all overflow-hidden group"
                            title={asset.name}
                          >
                            <img
                              src={`https://aistudio-api.wizice100.workers.dev${asset.thumbnailUrl}`}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-xs text-white text-center px-2 line-clamp-2">{asset.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveAsset(asset.id)
                              }}
                              className="absolute top-1 right-1 p-1 bg-accent-error/80 hover:bg-accent-error text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="제거"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </aside>

        {/* Toggle Left Panel */}
        {!leftPanelOpen && (
          <button
            onClick={() => setLeftPanelOpen(true)}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-bg-secondary border border-border-subtle rounded-r-lg p-2 z-10"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Canvas Area */}
        <main
          className="flex-1 bg-bg-primary overflow-auto flex items-center justify-center"
          style={{ background: 'repeating-conic-gradient(#222 0% 25%, #1a1a1a 0% 50%) 50% / 20px 20px' }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="p-8">
            <div className="shadow-2xl">
              <FabricCanvas
                ref={canvasRef}
                width={cover.width}
                height={cover.height}
                backgroundColor={cover.backgroundColor}
                zoom={zoom}
              />
            </div>
          </div>
        </main>

        {/* Right Panel - Layers & Properties */}
        <aside className={clsx(
          'bg-bg-secondary border-l border-border-subtle flex flex-col transition-all duration-200',
          rightPanelOpen ? 'w-72' : 'w-0'
        )}>
          {rightPanelOpen && (
            <>
              {/* Layers */}
              <div className="border-b border-border-subtle">
                <div className="p-3 flex items-center justify-between">
                  <h3 className="font-medium text-sm flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    레이어
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => addLayer({
                        type: 'image',
                        name: `레이어 ${(cover.layers.length || 0) + 1}`,
                        visible: true,
                        locked: false,
                        x: 50,
                        y: 50,
                        width: 200,
                        height: 200,
                        rotation: 0,
                        opacity: 1,
                      })}
                      className="p-1 text-text-tertiary hover:text-accent-gold"
                      title="새 레이어"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setRightPanelOpen(false)}
                      className="p-1 text-text-tertiary hover:text-text-primary"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="max-h-48 overflow-y-auto">
                  {cover.layers.slice().reverse().map((layer) => (
                    <div
                      key={layer.id}
                      onClick={() => setSelectedLayer(layer.id)}
                      className={clsx(
                        'group flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                        selectedLayerId === layer.id ? 'bg-bg-elevated' : 'hover:bg-bg-elevated/50'
                      )}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          updateLayer(layer.id, { visible: !layer.visible })
                        }}
                        className="text-text-tertiary hover:text-text-primary"
                      >
                        {layer.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          updateLayer(layer.id, { locked: !layer.locked })
                        }}
                        className="text-text-tertiary hover:text-text-primary"
                      >
                        {layer.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                      <div className="w-6 h-6 bg-bg-tertiary rounded flex items-center justify-center text-text-tertiary">
                        {layer.type === 'text' ? <Type className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                      </div>
                      <span className="flex-1 text-sm truncate">{layer.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeLayer(layer.id)
                        }}
                        className="text-text-tertiary hover:text-accent-error opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {cover.layers.length === 0 && (
                    <div className="p-4 text-center text-sm text-text-tertiary">
                      레이어가 없습니다
                    </div>
                  )}
                </div>
              </div>

              {/* Properties */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-3 flex items-center gap-2 border-b border-border-subtle">
                  <Settings2 className="w-4 h-4" />
                  <h3 className="font-medium text-sm">속성</h3>
                </div>

                {selectedLayer ? (
                  <div className="p-3 space-y-4">
                    {/* Name */}
                    <div>
                      <label className="text-xs text-text-tertiary block mb-1">이름</label>
                      <input
                        type="text"
                        value={selectedLayer.name}
                        onChange={(e) => updateLayer(selectedLayer.id, { name: e.target.value })}
                        className="input text-sm py-1"
                      />
                    </div>

                    {/* Position */}
                    <div>
                      <label className="text-xs text-text-tertiary block mb-1">위치</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-tertiary w-4">X</span>
                          <input
                            type="number"
                            value={selectedLayer.x}
                            onChange={(e) => updateLayer(selectedLayer.id, { x: Number(e.target.value) })}
                            className="input text-sm py-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-tertiary w-4">Y</span>
                          <input
                            type="number"
                            value={selectedLayer.y}
                            onChange={(e) => updateLayer(selectedLayer.id, { y: Number(e.target.value) })}
                            className="input text-sm py-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Size */}
                    <div>
                      <label className="text-xs text-text-tertiary block mb-1">크기</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-tertiary w-4">W</span>
                          <input
                            type="number"
                            value={selectedLayer.width}
                            onChange={(e) => updateLayer(selectedLayer.id, { width: Number(e.target.value) })}
                            className="input text-sm py-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-tertiary w-4">H</span>
                          <input
                            type="number"
                            value={selectedLayer.height}
                            onChange={(e) => updateLayer(selectedLayer.id, { height: Number(e.target.value) })}
                            className="input text-sm py-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Rotation */}
                    <div>
                      <label className="text-xs text-text-tertiary block mb-1">회전</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={360}
                          value={selectedLayer.rotation}
                          onChange={(e) => updateLayer(selectedLayer.id, { rotation: Number(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-sm w-12 text-right">{selectedLayer.rotation}°</span>
                        <button
                          onClick={() => updateLayer(selectedLayer.id, { rotation: 0 })}
                          className="p-1 text-text-tertiary hover:text-text-primary"
                          title="회전 초기화"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Opacity */}
                    <div>
                      <label className="text-xs text-text-tertiary block mb-1">투명도</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={selectedLayer.opacity}
                          onChange={(e) => updateLayer(selectedLayer.id, { opacity: Number(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-sm w-12 text-right">{Math.round(selectedLayer.opacity * 100)}%</span>
                      </div>
                    </div>

                    {/* Text specific properties */}
                    {selectedLayer.type === 'text' && (
                      <>
                        <div className="border-t border-border-subtle pt-4">
                          <label className="text-xs text-text-tertiary block mb-1">텍스트</label>
                          <textarea
                            value={selectedLayer.content || ''}
                            onChange={(e) => updateLayer(selectedLayer.id, { content: e.target.value })}
                            className="input text-sm py-1 resize-none"
                            rows={2}
                          />
                        </div>

                        <div>
                          <label className="text-xs text-text-tertiary block mb-1">폰트</label>
                          <select
                            value={selectedLayer.fontFamily || 'Noto Sans KR'}
                            onChange={(e) => updateLayer(selectedLayer.id, { fontFamily: e.target.value })}
                            className="input text-sm py-1"
                          >
                            {FONT_FAMILIES.map((font) => (
                              <option key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-text-tertiary block mb-1">크기</label>
                            <input
                              type="number"
                              value={selectedLayer.fontSize || 32}
                              onChange={(e) => updateLayer(selectedLayer.id, { fontSize: Number(e.target.value) })}
                              className="input text-sm py-1"
                              min={8}
                              max={200}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary block mb-1">굵기</label>
                            <select
                              value={selectedLayer.fontWeight || 400}
                              onChange={(e) => updateLayer(selectedLayer.id, { fontWeight: Number(e.target.value) })}
                              className="input text-sm py-1"
                            >
                              <option value={300}>Light</option>
                              <option value={400}>Regular</option>
                              <option value={500}>Medium</option>
                              <option value={700}>Bold</option>
                              <option value={900}>Black</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-text-tertiary block mb-1">색상</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={selectedLayer.fontColor || '#ffffff'}
                              onChange={(e) => updateLayer(selectedLayer.id, { fontColor: e.target.value })}
                              className="w-8 h-8 rounded border border-border-default cursor-pointer"
                            />
                            <input
                              type="text"
                              value={selectedLayer.fontColor || '#ffffff'}
                              onChange={(e) => updateLayer(selectedLayer.id, { fontColor: e.target.value })}
                              className="input text-sm py-1 flex-1"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-text-tertiary block mb-1">정렬</label>
                          <div className="flex gap-1">
                            {(['left', 'center', 'right'] as const).map((align) => (
                              <button
                                key={align}
                                onClick={() => updateLayer(selectedLayer.id, { textAlign: align })}
                                className={clsx(
                                  'flex-1 py-1 rounded text-sm transition-colors',
                                  selectedLayer.textAlign === align
                                    ? 'bg-accent-gold text-white'
                                    : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                                )}
                              >
                                {align === 'left' ? '좌' : align === 'center' ? '중' : '우'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="p-4 text-center text-sm text-text-tertiary">
                    레이어를 선택하세요
                  </div>
                )}

                {/* Background Color */}
                <div className="p-3 border-t border-border-subtle">
                  <label className="text-xs text-text-tertiary block mb-2">배경색</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={cover.backgroundColor}
                      onChange={(e) => setCover({ ...cover, backgroundColor: e.target.value })}
                      className="w-8 h-8 rounded border border-border-default cursor-pointer"
                    />
                    <input
                      type="text"
                      value={cover.backgroundColor}
                      onChange={(e) => setCover({ ...cover, backgroundColor: e.target.value })}
                      className="input text-sm py-1 flex-1"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </aside>

        {/* Toggle Right Panel */}
        {!rightPanelOpen && (
          <button
            onClick={() => setRightPanelOpen(true)}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-bg-secondary border border-border-subtle rounded-l-lg p-2 z-10"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
          </>
        ) : (
          /* AI Generation Panel */
          <AiGenerationPanel
            coverId={cover?.id || ''}
            coverWidth={cover?.width || 750}
            coverHeight={cover?.height || 1100}
            paletteAssets={palette?.assets || []}
            userPoints={points}
            onGenerationComplete={handleAiGenerationComplete}
          />
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-sm w-full p-6 m-4">
            <h3 className="text-lg font-bold mb-4">이미지 내보내기</h3>
            <div className="space-y-3">
              <button
                onClick={() => handleExport('png')}
                className="w-full btn btn-secondary justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                PNG (투명 배경 지원)
              </button>
              <button
                onClick={() => handleExport('jpeg', 0.9)}
                className="w-full btn btn-secondary justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                JPEG (고품질)
              </button>
              <button
                onClick={() => handleExport('jpeg', 0.7)}
                className="w-full btn btn-secondary justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                JPEG (웹 최적화)
              </button>
            </div>
            <button
              onClick={() => setShowExportModal(false)}
              className="w-full btn btn-ghost mt-4"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* Asset Browser Modal */}
      {showAssetBrowserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setShowAssetBrowserModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-bg-secondary rounded-xl w-full max-w-6xl h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h2 className="text-xl font-bold">
                {assetBrowserType === 'cover' && '통합 표지 에셋 선택'}
                {assetBrowserType === 'background' && '배경 에셋 선택'}
                {assetBrowserType === 'character' && '인물 에셋 선택'}
                {assetBrowserType === 'typography' && '타이포 에셋 선택'}
                {assetBrowserType === 'effect' && '효과 에셋 선택'}
              </h2>
              <button
                onClick={() => setShowAssetBrowserModal(false)}
                className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={`/assets?type=${assetBrowserType}&embedMode=true`}
                className="w-full h-full border-0"
                title="Asset Browser"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
